import { useEffect, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { updateDog } from "~/api/dogs"
import { attachPortraitSourcePhoto } from "~/api/portrait"
import { getKibbleCurrent, type KibbleCurrentResponse } from "~/api/kibble"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { heroPortraitUrl } from "~/utils/heroPortraitUrl"
import { CheckCircle, Palette, X } from "lucide-react"
import { DashboardAskDynamoCard } from "~/components/DashboardAskDynamoCard"
import { DashboardSharePanel } from "~/components/DashboardSharePanel"
import { HeroPortraitFrame } from "~/components/HeroPortraitFrame"
import { HomeScreenInstallBanner } from "~/components/HomeScreenInstallBanner"
import { DogSelector } from "~/components/DogSelector"
import { PlusWelcomeCelebration } from "~/components/PlusWelcomeCelebration"
import { usePortraitCelebrationRedirect } from "~/hooks/usePortraitCelebrationRedirect"
import { ENTITLEMENTS, hasEntitlement } from "~/utils/entitlements"

const PORTRAIT_STYLES = [
  {
    id: "personality",
    label: "Personality Portrait",
    description: "The classic portrait based on your dog’s personality.",
  },
  {
    id: "seasonal",
    label: "Seasonal Portrait",
    description: "A seasonal Image of the Month for Plus members.",
  },
] as const

export function DashboardPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [kibble, setKibble] = useState<KibbleCurrentResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [emailVerifiedBanner, setEmailVerifiedBanner] = useState(false)
  const [styleSheetOpen, setStyleSheetOpen] = useState(false)
  const [savingStyle, setSavingStyle] = useState(false)
  const [styleError, setStyleError] = useState<string | null>(null)
  const [sheetPhoto, setSheetPhoto] = useState<File | null>(null)
  const [sheetPreviewUrl, setSheetPreviewUrl] = useState<string | null>(null)
  const [sheetUploadError, setSheetUploadError] = useState<string | null>(null)
  const [sheetUploading, setSheetUploading] = useState(false)

  useEffect(() => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        if (profileData.user.needs_free_dog_choice) {
          navigate("/choose-free-dog", { replace: true })
          return
        }
        setProfile(profileData)
        setKibble(kibbleData)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (searchParams.get("email_verified") !== "1") return
    getProfile()
      .then((data) => {
        setProfile(data)
        if (data.user.email_verified) setEmailVerifiedBanner(true)
      })
      .catch(() => {})
      .finally(() => {
        const next = new URLSearchParams(searchParams)
        next.delete("email_verified")
        setSearchParams(next, { replace: true })
      })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    if (!emailVerifiedBanner) return
    const id = window.setTimeout(() => setEmailVerifiedBanner(false), 12_000)
    return () => window.clearTimeout(id)
  }, [emailVerifiedBanner])

  useEffect(() => {
    if (!profile?.dog_profile?.portrait || profile.dog_profile.portrait.status !== "generating") return
    const interval = window.setInterval(() => {
      getProfile()
        .then(setProfile)
        .catch(() => {})
    }, 3000)
    return () => window.clearInterval(interval)
  }, [profile?.dog_profile?.portrait?.status])

  usePortraitCelebrationRedirect(profile, navigate, !loading && Boolean(profile))

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const archetypeName = dog?.archetype_label ?? "Archetype"
  const archetypeIcon = dog?.archetype_icon
  const heroImage = heroPortraitUrl(archetypeIcon, dog?.portrait)
  const hasPlusEntitlements = profile.user.entitlements.length > 0
  const canSelectSeasonal = hasEntitlement(
    profile.user.entitlements,
    ENTITLEMENTS.seasonalPortraits,
  )
  const seasonalPortraitAvailable = profile.seasonal_portrait_available ?? false
  const selectedStyle = dog?.selected_portrait_style || "personality"
  const showPlusWelcome =
    hasPlusEntitlements && !profile.user.plus_welcome_seen_at

  const handleDogChanged = () => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setKibble(kibbleData)
      })
      .catch(() => {})
  }

  async function dismissPlusWelcome() {
    try {
      const { markPlusWelcomeSeen } = await import("~/api/plus")
      const result = await markPlusWelcomeSeen()
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              user: {
                ...prev.user,
                plus_welcome_seen_at: result.plus_welcome_seen_at,
              },
            }
          : prev,
      )
    } catch {
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              user: { ...prev.user, plus_welcome_seen_at: new Date().toISOString() },
            }
          : prev,
      )
    }
  }

  async function handleSelectPortraitStyle(styleId: string) {
    const dogId = dog?.id ?? profile?.active_dog_profile_id
    if (!dogId) return
    if (styleId === "seasonal") {
      if (!canSelectSeasonal) {
        setStyleSheetOpen(false)
        navigate("/plus")
        return
      }
      if (!seasonalPortraitAvailable) {
        return
      }
    }
    if (styleId === selectedStyle) {
      setStyleSheetOpen(false)
      return
    }

    setSavingStyle(true)
    setStyleError(null)
    try {
      const result = await updateDog(dogId, { selected_portrait_style: styleId })
      setProfile((prev) =>
        prev?.dog_profile
          ? {
              ...prev,
              dog_profile: {
                ...prev.dog_profile,
                selected_portrait_style: result.dog.selected_portrait_style,
              },
            }
          : prev,
      )
      setStyleSheetOpen(false)
    } catch (err) {
      setStyleError(err instanceof Error ? err.message : "Could not update portrait style")
    } finally {
      setSavingStyle(false)
    }
  }

  function openPortraitStyleSheet() {
    setStyleError(null)
    setSheetUploadError(null)
    setSheetPhoto(null)
    setSheetPreviewUrl(null)
    setStyleSheetOpen(true)
  }

  async function handleAttachSourcePhoto() {
    if (!sheetPhoto || sheetUploading) return
    setSheetUploading(true)
    setSheetUploadError(null)
    try {
      const updated = await attachPortraitSourcePhoto(sheetPhoto)
      setProfile(updated)
      setSheetPhoto(null)
      setSheetPreviewUrl(null)
    } catch (err) {
      setSheetUploadError(err instanceof Error ? err.message : "Could not save photo")
    } finally {
      setSheetUploading(false)
    }
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <HomeScreenInstallBanner enableFromEmailVerification={emailVerifiedBanner} />
        {showPlusWelcome && (
          <PlusWelcomeCelebration profile={profile} onDismiss={() => void dismissPlusWelcome()} />
        )}
        {emailVerifiedBanner && (
          <div
            className="mb-4 flex gap-3 rounded-lg border border-emerald-800/20 bg-emerald-50/90 p-4 text-sm text-emerald-950 shadow-sm"
            role="status"
          >
            <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="font-medium text-emerald-950">Email verified</p>
              <p className="mt-1 text-emerald-900/90">
                Thanks — your address is confirmed. You&apos;re all set to use Doggy Dynamo with this account.
              </p>
              <button
                type="button"
                className="mt-3 text-sm font-medium text-emerald-900 underline decoration-emerald-800/40 underline-offset-2 hover:decoration-emerald-900"
                onClick={() => setEmailVerifiedBanner(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        {!profile.user.email_verified && (
          <div className="mb-4 rounded-lg border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-sm text-[var(--color-text-secondary)]">
            <p className="font-medium text-[var(--color-text-primary)]">Verify your email</p>
            <p className="mt-1">
              <Link to="/verify-email" className="font-medium text-[var(--color-text-primary)] underline">
                Open the verification page
              </Link>{" "}
              to resend the message or confirm we have the right address.
            </p>
          </div>
        )}
        <div className="flex justify-center">
          <DogSelector onDogChanged={handleDogChanged} />
        </div>

        {dog ? (
          <>
            <p className="mt-4 text-center text-xl text-[var(--color-text-secondary)]">{archetypeName}</p>

            {heroImage ? (
              <div className="relative mt-4">
                <HeroPortraitFrame
                  src={heroImage}
                  alt={`${archetypeName} hero`}
                />
                <button
                  type="button"
                  onClick={openPortraitStyleSheet}
                  className="absolute bottom-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-[var(--color-surface-card)]/95 px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] shadow-sm ring-1 ring-[var(--color-divider)]"
                >
                  <Palette size={14} aria-hidden />
                  Portrait Style
                </button>
              </div>
            ) : (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={openPortraitStyleSheet}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[var(--color-surface-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-primary)] shadow-sm ring-1 ring-[var(--color-divider)]"
                >
                  <Palette size={14} aria-hidden />
                  Portrait Style
                </button>
              </div>
            )}

            <DashboardSharePanel
              dogName={dogName}
              archetypeLabel={archetypeName}
              assessmentSessionToken={dog.assessment_session_token}
              archetypeIcon={archetypeIcon}
              portrait={dog.portrait}
            />

            {profile.ask_dynamo && (
              <DashboardAskDynamoCard
                dogName={dogName}
                dogSex={dog.sex}
                dailyRemaining={profile.ask_dynamo.daily_remaining}
                dailyLimit={profile.ask_dynamo.daily_limit}
                showPlusCta={!hasPlusEntitlements}
              />
            )}

            <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4">
              {kibble?.perspective ? (
                <>
                  <p className="text-sm text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-1 text-xl font-semibold text-[var(--color-text-primary)]">
                    {kibble.perspective.title ?? `${archetypeName} Perspective`}
                  </h2>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Updated weekly for {dogName}
                  </p>
                  <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                    {`${kibble.perspective.body.slice(0, 140)}${kibble.perspective.body.length > 140 ? "…" : ""}`}
                  </p>
                  <Link
                    to="/perspectives?expand=perspective"
                    className="mt-3 inline-flex rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)]"
                  >
                    Read {dogName}&apos;s Perspective
                  </Link>
                </>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-2 text-xl font-medium text-[var(--color-text-primary)]">No perspective yet</h2>
                  <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                    Your first perspective will appear here after your next scheduled delivery.
                  </p>
                </div>
              )}
              {dog.assessment_session_token ? (
                <Link
                  to={`/results?token=${encodeURIComponent(dog.assessment_session_token)}`}
                  className="mt-3 inline-flex w-full justify-center rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] transition hover:opacity-90"
                >
                  View {dogName}&apos;s Full Profile
                </Link>
              ) : null}
            </section>

            <section className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4">
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">
                Suggested Tip for {dogName}
              </h3>
              {kibble?.tip ? (
                <div className="mt-2 rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">{kibble.tip.title}</p>
                  <p className="text-sm text-[var(--color-text-secondary)]">Updated this week</p>
                  <Link
                    to="/perspectives?expand=idea"
                    className="mt-2 inline-flex rounded-full bg-[var(--color-text-primary)] px-3 py-1 text-xs font-medium text-[var(--color-surface-card)]"
                  >
                    View tip
                  </Link>
                </div>
              ) : (
                <div className="mt-2 text-center">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">No tip yet</p>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Your first tip will appear here after your next scheduled delivery.
                  </p>
                </div>
              )}
            </section>

            {!hasPlusEntitlements && (
              <div className="mt-4 text-center">
                <Link
                  to="/plus"
                  className="text-sm font-semibold text-[var(--color-text-primary)] underline decoration-[var(--color-text-primary)]/40 underline-offset-2 hover:decoration-[var(--color-text-primary)]"
                >
                  ✨ Discover Doggy Dynamo Plus →
                </Link>
              </div>
            )}
          </>
        ) : (
          <p className="mt-6 text-[var(--color-text-secondary)]">
            No dog profile yet. Complete an assessment to save your dog&apos;s profile.
          </p>
        )}

      </LoggedInScreenCard>

      {styleSheetOpen && dog && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center"
          onClick={() => setStyleSheetOpen(false)}
          role="presentation"
        >
          <div
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-[var(--color-surface-card)] p-4 shadow-xl"
            role="dialog"
            aria-labelledby="portrait-style-heading"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h2
                id="portrait-style-heading"
                className="text-lg font-semibold text-[var(--color-text-primary)]"
              >
                Portrait Style
              </h2>
              <button
                type="button"
                onClick={() => setStyleSheetOpen(false)}
                className="rounded-full p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-2">
              {PORTRAIT_STYLES.map((style) => {
                const locked = style.id === "seasonal" && !canSelectSeasonal
                const comingSoon = style.id === "seasonal" && canSelectSeasonal && !seasonalPortraitAvailable
                const selected = selectedStyle === style.id && !comingSoon
                const disabled = savingStyle || comingSoon
                return (
                  <button
                    key={style.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => void handleSelectPortraitStyle(style.id)}
                    className={`w-full rounded-xl border p-3 text-left transition ${
                      selected
                        ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)]/5"
                        : "border-[var(--color-divider)] bg-[var(--color-bg-primary)] hover:bg-[var(--color-surface-hover)]"
                    } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
                  >
                    <p className="font-medium text-[var(--color-text-primary)]">
                      {style.label}
                      {locked ? " (Plus)" : comingSoon ? " (Coming soon)" : ""}
                      {selected ? " · Selected" : ""}
                    </p>
                    <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                      {comingSoon
                        ? "Seasonal portraits are coming soon for Plus members."
                        : style.description}
                    </p>
                  </button>
                )
              })}
            </div>

            {dog.portrait.hero_refresh_queued && dog.portrait.status !== "generating" && (
              <p className="mt-3 text-sm text-[var(--color-text-secondary)]" role="status">
                A scheduled portrait refresh is queued — we&apos;ll update {dogName}&apos;s portrait soon.
              </p>
            )}

            <div className="mt-4 rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-3">
              <p className="text-sm font-medium text-[var(--color-text-primary)]">Source photo</p>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                {dog.portrait.has_source_photo
                  ? "A source photo is on file for personalised and seasonal portraits."
                  : "No source photo yet. Add one to unlock personalised portraits — selecting a style alone does not generate a new image."}
              </p>
              {!dog.portrait.has_source_photo && (
                <div className="mt-3">
                  <PhotoUploadPanel
                    dogName={dogName}
                    photo={sheetPhoto}
                    previewUrl={sheetPreviewUrl}
                    error={sheetUploadError}
                    required
                    onPhotoSelected={(file, preview) => {
                      setSheetPhoto(file)
                      setSheetPreviewUrl(preview)
                      setSheetUploadError(null)
                    }}
                    onClear={() => {
                      setSheetPhoto(null)
                      setSheetPreviewUrl(null)
                    }}
                  />
                  <button
                    type="button"
                    disabled={!sheetPhoto || sheetUploading}
                    onClick={() => void handleAttachSourcePhoto()}
                    className="mt-3 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2 text-sm font-medium text-[var(--color-surface-card)] disabled:opacity-50"
                  >
                    {sheetUploading ? "Saving…" : "Save source photo"}
                  </button>
                </div>
              )}
            </div>

            {!hasPlusEntitlements && (
              <div className="mt-4 text-center">
                <Link
                  to="/plus"
                  onClick={() => setStyleSheetOpen(false)}
                  className="text-sm font-semibold text-[var(--color-brand-primary)] underline"
                >
                  Discover Doggy Dynamo Plus →
                </Link>
              </div>
            )}

            {styleError && (
              <p className="mt-3 text-sm text-red-600" role="alert">
                {styleError}
              </p>
            )}
          </div>
        </div>
      )}

    </PageShell>
  )
}
